import { useState, useEffect } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { taskAPI, taskAttachmentAPI, aiChatAPI } from '../lib/api';
import { sortClientsAlpha } from '../lib/sortClients';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import { StellarSelect, StellarDatePicker } from './StellarFormControls';
import { TaskStatusIndicator } from './TaskStatusIndicator';
import {
  Clock, Calendar, Trash2, Timer, Eye,
  ChevronRight, ChevronDown, MessageSquare, Link2, Paperclip, User,
  Edit3, Copy, X, Plus, Lock, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { formatTaskDate, formatTimeWithTz, formatDateTimeLong } from '../lib/dateFormat';
import { NotionEditor } from './NotionEditor';
import { TaskSubmissionsSection } from './TaskSubmissions';
import { AssigneeMultiSelect } from './AssigneeMultiSelect';
import { TaskTransferPanel } from './TaskTransferPanel';
import { LegacySubtasksSection } from './LegacySubtasksSection';
import { TaskRichCommentBox, TaskCommentAttachments } from './TaskRichCommentBox';
import { AutoGrowTextarea } from './AutoGrowTextarea';
import { AttachmentsField } from './AttachmentsField';
import { AttachmentsDisplay } from './AttachmentsDisplay';
import { usePresenceTracker } from '../lib/usePresenceTracker';

const PRIORITY_DOT = { high: 'bg-red-500/70', medium: 'bg-gold/70', low: 'bg-foreground/20' };
const STATUS_LABELS = { not_started: 'Not Started', in_progress: 'In Progress', waiting: 'In Progress', review: 'Review', completed: 'Completed', cancelled: 'Cancelled' };
// 2026-07-31 — 'waiting' removed as a user-facing status (attendance pause
// still uses it internally; it displays as In Progress).
const STATUSES = ['not_started', 'in_progress', 'review', 'completed'];

export function TaskDetailSheet({ taskId: propTaskId, open, onOpenChange, onUpdate, focusSubmissionId, focusCommentId }) {
  const { currentWorkspace, workspaceRole } = useWorkspace();
  const { user } = useAuth();
  // Internal `taskId` mirrors the prop; resyncs whenever the prop changes
  // (external open) or the sheet closes, so re-opening works correctly.
  const [taskId, setTaskId] = useState(propTaskId);
  useEffect(() => { setTaskId(propTaskId); }, [propTaskId]);
  useEffect(() => { if (!open) setTaskId(propTaskId); }, [open, propTaskId]);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editMore, setEditMore] = useState(false);
  // Tracks whether the user manually clicked a Priority pill in this edit session.
  // false → auto-suggest from due_date and submit with priority_locked unchanged.
  // true  → submit with priority_locked=true (cron will skip).
  const [editPriorityManuallySet, setEditPriorityManuallySet] = useState(false);
  // True if user clicked any priority pill in this edit session (selection OR deselection).
  // Used so saveEdit can write priority_locked=false when the user explicitly unlocks via deselect.
  const [editPriorityTouched, setEditPriorityTouched] = useState(false);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    if (!taskId || !currentWorkspace?.id || !open) return;
    setLoading(true);
    taskAPI.get(currentWorkspace.id, taskId)
      .then(res => setTask(res.data))
      .catch(() => toast.error('Failed to load task'))
      .finally(() => setLoading(false));
  }, [taskId, currentWorkspace?.id, open]);

  // 2026-06-16 — Team Pulse presence ping. When the detail sheet is OPEN
  // with a real task id, we ping `/api/presence/viewing-task` so Team Pulse
  // can show "Working on: <this task title>" within 30 s. When the sheet
  // closes (open=false) we pass `null` to clear presence ("Not on a task").
  // The hook handles 30 s heartbeats while open and final cleanup on unmount.
  usePresenceTracker(currentWorkspace?.id, open ? taskId : null);

  // Re-fetch helper for D87 child components (transfer panel, rich comment box).
  const loadTask = async () => {
    if (!taskId || !currentWorkspace?.id) return;
    try {
      const res = await taskAPI.get(currentWorkspace.id, taskId);
      setTask(res.data);
    } catch (_) {}
  };

  // Fetch members + clients for edit dropdowns
  useEffect(() => {
    if (!currentWorkspace?.id || !open) return;
    import('../lib/api').then(({ workspaceAPI, clientGroupAPI }) => {
      // 2026-06-05 PROD HOTFIX — un-scoped assignee picker (assignee_picker_unscoped).
      // Manager / Team users now see the full roster in the edit assignee field.
      workspaceAPI.listAssignableMembers(currentWorkspace.id).then(r => setMembers((r.data || []).map(m => m.user).filter(Boolean))).catch(() => {});
      clientGroupAPI.list(currentWorkspace.id).then(r => setClients(sortClientsAlpha(r.data || []))).catch(() => {});
    });
  }, [currentWorkspace?.id, open]);

  const openEdit = () => {
    if (!task) return;
    setEditData({
      title: task.title || '', description: task.description || '',
      assigned_to: task.assigned_to || [], client_group_id: task.client_group_id || '',
      accountable_to: task.accountable_to || [],
      due_date: task.due_date?.slice(0, 10) || '', priority: task.priority || 'medium',
      status: task.status || 'not_started', labels: task.labels || [],
      estimated_hours: task.estimated_hours || 0, recurrence: task.recurrence || '',
      external_links: task.external_links?.length ? task.external_links : [{ name: '', url: '' }],
      internal_links: task.internal_links?.length ? task.internal_links : [{ name: '', path: '' }],
      drive_link: task.drive_link || '',
      // Batch 2 R1 (BUG-RW-CT-017) — Deliverable Type was missing from the
      // side-pane edit dialog. Now editable here so users can re-classify
      // existing tasks without opening the create modal again.
      deliverable_type: task.deliverable_type || '',
      follow_ups: task.follow_ups?.length ? task.follow_ups : [],
      attachments: task.attachments || [],
    });
    // Inherit existing lock state — pre-locked tasks remain locked unless user explicitly unlocks
    // by changing priority manually (which sets it to true again — same value, OK).
    setEditPriorityManuallySet(false);
    setEditPriorityTouched(false);
    setShowEdit(true);
  };

  // Live auto-suggest priority from due_date in the Edit dialog (unless user manually overrode
  // OR the task is already priority_locked — pre-locked tasks shouldn't auto-flip on edit).
  useEffect(() => {
    if (!editData || editPriorityManuallySet || task?.priority_locked) return;
    if (!editData.due_date) return;
    try {
      const due = new Date(editData.due_date + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const days = Math.floor((due - today) / 86400000);
      let suggested = 'low';
      if (days < 0 || days <= 5) suggested = 'high';
      else if (days <= 10) suggested = 'medium';
      if (suggested !== editData.priority) {
        setEditData(p => ({ ...p, priority: suggested }));
      }
    } catch {}
  }, [editData?.due_date, editPriorityManuallySet, task?.priority_locked]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEdit = async () => {
    if (!editData || !task) return;
    // Validate follow-up dates are before due_date
    const validFu = (editData.follow_ups || []).filter(f => f.date);
    if (validFu.length && editData.due_date) {
      const bad = validFu.find(f => f.date >= editData.due_date);
      if (bad) { toast.error(`Follow-up "${bad.label || bad.date}" must be before the due date`); return; }
    }
    try {
      const payload = { ...editData };
      // Clean empty links
      payload.external_links = (payload.external_links || []).filter(l => l.name || l.url);
      payload.internal_links = (payload.internal_links || []).filter(l => l.name || l.path);
      payload.follow_ups = validFu;
      // Lock priority based on user action in this edit session:
      //   touched + manually set → lock
      //   touched + deselected   → unlock
      //   not touched            → leave existing lock state alone
      if (editPriorityTouched) payload.priority_locked = editPriorityManuallySet;
      await taskAPI.update(currentWorkspace.id, task.id, payload);
      const res = await taskAPI.get(currentWorkspace.id, task.id);
      setTask(res.data);
      setShowEdit(false);
      setEditPriorityManuallySet(false);
      setEditPriorityTouched(false);
      toast.success('Task updated');
      if (onUpdate) onUpdate();
    } catch (err) { toast.error(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to save'); }
  };

  const duplicateTask = async () => {
    if (!task) return;
    try {
      await taskAPI.create({
        workspace_id: currentWorkspace.id,
        title: `${task.title} (copy)`,
        description: task.description || '',
        assigned_to: task.assigned_to || [],
        client_group_id: task.client_group_id || '',
        priority: task.priority || 'medium',
        status: 'not_started',
        labels: task.labels || [],
        estimated_hours: task.estimated_hours || 0,
        recurrence: task.recurrence || '',
        external_links: task.external_links || [],
        internal_links: task.internal_links || [],
        drive_link: task.drive_link || '',
      });
      toast.success('Task duplicated');
      if (onUpdate) onUpdate();
    } catch { toast.error('Failed to duplicate'); }
  };

  const handleStatusChange = async (newStatus) => {
    if (!task) return;
    try {
      await taskAPI.update(currentWorkspace.id, task.id, { status: newStatus });
      setTask(prev => ({ ...prev, status: newStatus }));
      toast.success(`Moved to ${STATUS_LABELS[newStatus]}`);
      if (onUpdate) onUpdate();
    } catch { toast.error('Failed to update'); }
  };

  const handleComment = async () => {
    if (!task || !comment.trim()) return;
    try {
      await taskAPI.addComment(currentWorkspace.id, task.id, comment);
      setComment('');
      const res = await taskAPI.get(currentWorkspace.id, task.id);
      setTask(res.data);
      toast.success('Comment added');
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async () => {
    if (!task) return;
    try {
      await taskAPI.delete(currentWorkspace.id, task.id);
      toast.success('Task deleted');
      onOpenChange(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      // Iter 68 C6: surface backend permission denial as an actionable toast
      // rather than the previous generic "Failed".
      const detail = err?.response?.data?.detail || 'Failed to delete task';
      toast.error(detail);
    }
  };

  // Iter 68 C6: visibility rule for Delete buttons.
  // Owner/Admin: can delete any task. Manager/Team: can delete only tasks they created.
  const canDeleteTask = (() => {
    if (!task || !user) return false;
    if (workspaceRole === 'owner' || workspaceRole === 'admin') return true;
    return task.created_by === user.id;
  })();

  // Phase 3B Batch 1 T-HIGH-1 — Team role can only change status / add submissions
  // / comments / time-logs. Title / description / assignees / client / due_date /
  // priority are frozen at creation for Team. Hide the full "Edit Task" button
  // entirely for Team. Server PUT /api/tasks rejects payload fields other than
  // `status` for Team as a second line of defense.
  const canEditTaskMetadata = (() => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return ['owner', 'admin', 'manager'].includes(workspaceRole);
  })();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* Batch 1 ITEM 3 (BUG-RW-T-004..011) — root cause for the 11 clipped
          buttons: Sheet base component has `sm:max-w-sm` (384px cap) which
          silently dominated our w-[540px]. `sm:max-w-[600px]` lets the
          intended 540/600px width actually take effect at >= 640px viewports.
          Also `min-w-0` on the inner flex column so children honor the
          parent width instead of overflowing it. */}
      <SheetContent className="task-detail-sheet-mobile no-mobile-cap !w-full !max-w-full sm:!w-[600px] sm:!max-w-[600px] p-0 overflow-hidden flex flex-col min-w-0" data-testid="task-detail-sheet">
        {/* BUG-T-007 / BUG-T-015 — a11y: SheetContent extends Radix Dialog so
            it requires a Title + Description. We render them visually hidden
            (sr-only) so the existing visual header isn't disturbed. */}
        <SheetHeader className="sr-only">
          <SheetTitle>{task?.title || 'Task details'}</SheetTitle>
          <SheetDescription>
            {task ? `Task detail panel for "${task.title}". Use this panel to edit status, assignees, log time, and review submissions.` : 'Loading task details.'}
          </SheetDescription>
        </SheetHeader>
        {loading || !task ? (
          <div className="flex items-center justify-center h-full"><Clock className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            {/* Header — D54 B1: extra right padding so content never slides under the close button. */}
            <div className="px-6 pt-5 pb-4 pr-12 border-b border-border/50 shrink-0">
              <div className="flex items-center gap-2 mb-2">
                {task.status === 'completed' || task.status === 'cancelled' ? (
                  <TaskStatusIndicator status={task.status} size="sm" />
                ) : (
                  <>
                    <span className={`h-2.5 w-2.5 rounded-full ${PRIORITY_DOT[task.priority]}`} />
                    <span className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium">{task.priority} priority</span>
                    <span className="text-[11px] text-muted-foreground mx-1">·</span>
                    <TaskStatusIndicator status={task.status} size="sm" />
                  </>
                )}
                {task.recurrence && task.recurrence !== 'none' && task.recurrence !== '' && (
                  <Badge variant="outline" className="text-[10px] font-normal capitalize">{task.recurrence}</Badge>
                )}
              </div>
              <h2 className="text-lg font-semibold tracking-tight leading-snug">{task.title}</h2>
              {task.due_date && task.due_date.slice(0,10) < new Date().toISOString().slice(0,10) && task.status !== 'completed' && task.status !== 'cancelled' && <span className="text-[9px] px-2 py-0.5 rounded bg-red-500/15 text-red-400 font-bold mt-1 inline-block">OVERDUE</span>}
              <div className="flex items-center gap-2 mt-2">
                {task.status !== 'completed' && task.status !== 'cancelled' && task.priority_locked && <Lock className="h-3 w-3 text-muted-foreground/40" data-testid="priority-locked-icon" />}
                {canEditTaskMetadata && (
                  <Button variant="outline" size="sm" className="h-7 text-[11px] border-border/30" onClick={openEdit} data-testid="edit-task-btn"><Edit3 className="h-3 w-3 mr-1" /> Edit</Button>
                )}
                <Button variant="outline" size="sm" className="h-7 text-[11px] border-border/30" onClick={duplicateTask}><Copy className="h-3 w-3 mr-1" /> Duplicate</Button>
                {task.status !== 'completed' && task.status !== 'cancelled' && task.priority_locked && <Button variant="ghost" size="sm" className="h-7 text-[10px] text-gold" onClick={async () => { try { await taskAPI.update(currentWorkspace.id, task.id, { priority_locked: false }); const r = await taskAPI.get(currentWorkspace.id, task.id); setTask(r.data); toast.success('Auto-priority unlocked'); if (onUpdate) onUpdate(); } catch { toast.error('Failed'); } }}>Unlock auto-priority</Button>}
                {canDeleteTask && (
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-[11px] text-red-400 ml-auto" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (confirm('Delete this task?')) handleDelete(); }}>Delete</Button>
                )}
              </div>
              {/* Follow-up progress bar */}
              {task.follow_ups && task.follow_ups.length > 0 && (
                <div className="flex items-center gap-1 mt-3" data-testid="followup-progress">
                  {task.follow_ups.map((fu, i) => (<div key={i} className="flex items-center gap-1"><div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center text-[8px] ${fu.completed ? 'border-green-500 bg-green-500/15 text-green-500' : fu.date <= new Date().toISOString().slice(0,10) ? 'border-gold bg-gold/15 text-gold' : 'border-border/30 text-muted-foreground/30'}`}>{fu.completed ? <Check className="h-2.5 w-2.5" /> : i+1}</div>{i < task.follow_ups.length && <div className="w-8 h-0.5 bg-border/20" />}</div>))}
                  <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center text-[7px] ${task.status === 'completed' ? 'border-green-500 bg-green-500/15 text-green-500' : 'border-gold/30 text-muted-foreground/30'}`}>{task.status === 'completed' ? <Check className="h-2.5 w-2.5" /> : 'D'}</div>
                  <span className="text-[9px] text-muted-foreground/30 ml-1">Due: {task.due_date?.slice(0,10)}</span>
                </div>
              )}
            </div>

            <ScrollArea className="flex-1">
              <div className="px-6 py-5 space-y-5">
                {/* Properties — RD2 (B5/CT-012): 1-col on mobile, 2-col on sm+. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-[13px]" data-testid="task-overview-properties">
                  <div><span className="text-muted-foreground text-[11px] block mb-0.5">Due</span><span>{formatTaskDate(task.due_date) || '—'}</span></div>
                  <div><span className="text-muted-foreground text-[11px] block mb-0.5">Start</span><span>{formatTaskDate(task.start_date) || '—'}</span></div>
                  <div><span className="text-muted-foreground text-[11px] block mb-0.5">Priority</span><span className="capitalize">{task.priority || 'medium'}</span></div>
                  {task.project_detail && <div><span className="text-muted-foreground text-[11px] block mb-0.5">Project</span><span>{task.project_detail.name}</span></div>}
                </div>

                {/* 2026-07 — Timer / auto-tracking / time-sessions UI fully removed (owner directive). */}

                {/* Assignees */}
                {(task.assignee_details || []).length > 0 && (
                  <div>
                    <span className="text-muted-foreground text-[11px] block mb-1.5">Assigned to</span>
                    <div className="flex flex-wrap gap-1.5">
                      {task.assignee_details.map(a => (
                        <div key={a.id} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-secondary">
                          <span className="text-[12px] font-medium">{a.name}</span>
                          {a.timezone && <span className="text-[10px] text-muted-foreground/60">{formatTimeWithTz(a.timezone)}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* FEATURE 5 (2026-06-04) — Creator + assignment chain.
                    Always visible. Shows "Created by <name>" and, when
                    different, "Assigned by <name> to <assignee names>". */}
                {(task.creator_detail || task.assigner_detail) && (
                  <div className="text-[11px] text-muted-foreground/80 space-y-0.5" data-testid="task-creator-assigner-chain">
                    {task.creator_detail && (
                      <div data-testid="task-created-by">
                        <span className="text-muted-foreground/60">Created by </span>
                        <span className="font-medium text-foreground/80">{task.creator_detail.name}</span>
                      </div>
                    )}
                    {task.assigner_detail && (task.assignee_details || []).length > 0 && (
                      <div data-testid="task-assigned-by-chain">
                        <span className="text-muted-foreground/60">Assigned by </span>
                        <span className="font-medium text-foreground/80">{task.assigner_detail.name}</span>
                        <span className="text-muted-foreground/60"> to </span>
                        <span className="font-medium text-foreground/80">
                          {(task.assignee_details || []).map((a) => a.name).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Tags */}
                {(task.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1" data-testid="task-detail-tags">
                    {task.tags.map(t => <Badge key={t} variant="outline" className="text-[10px] font-normal">{t}</Badge>)}
                  </div>
                )}

                {/* 2026-07 — Auto-Assign automation banner: pending suggestion
                    on an unassigned task. Owner/Admin/Manager only. */}
                {task.assign_suggestion && task.assign_suggestion.status === 'pending' && (task.assigned_to || []).length === 0 &&
                  (user?.role === 'super_admin' || ['owner', 'admin', 'manager'].includes(workspaceRole)) && (
                  <div className="rounded-lg border border-gold/25 bg-gold/[0.05] px-3 py-2.5 flex flex-wrap items-center gap-2" data-testid="assign-suggestion-banner">
                    <div className="flex-1 min-w-[180px]">
                      <p className="text-[12px] text-foreground/90">
                        <span className="text-gold font-medium">Suggested assignee:</span>{' '}
                        {task.assign_suggestion.name}
                        <span className="text-muted-foreground/60"> ({task.assign_suggestion.active_tasks} active {task.assign_suggestion.active_tasks === 1 ? 'task' : 'tasks'})</span>
                      </p>
                      {task.assign_suggestion.backup && (
                        <p className="text-[10.5px] text-muted-foreground/55 mt-0.5">
                          Backup: {task.assign_suggestion.backup.name} ({task.assign_suggestion.backup.active_tasks} active)
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        type="button" size="sm"
                        className="h-7 text-[11px] bg-gold text-black hover:bg-gold/90"
                        data-testid="assign-suggestion-accept-btn"
                        onClick={async () => {
                          try {
                            await aiChatAPI.resolveAssignSuggestion(currentWorkspace?.id || task.workspace_id, task.id, 'accept');
                            toast.success(`Assigned to ${task.assign_suggestion.name}`);
                            loadTask(); onUpdate && onUpdate();
                          } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to assign'); }
                        }}
                      >
                        Assign {(task.assign_suggestion.name || '').split(' ')[0]}
                      </Button>
                      <Button
                        type="button" size="sm" variant="ghost"
                        className="h-7 text-[11px] text-muted-foreground/70"
                        data-testid="assign-suggestion-dismiss-btn"
                        onClick={async () => {
                          try {
                            await aiChatAPI.resolveAssignSuggestion(currentWorkspace?.id || task.workspace_id, task.id, 'dismiss');
                            loadTask();
                          } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to dismiss'); }
                        }}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                )}

                {/* Assignees */}
                {(task.assignee_details || []).length > 0 && (
                  <div data-testid="task-detail-assignees">
                    <span className="text-muted-foreground text-[11px] block mb-1.5 flex items-center gap-1"><User className="h-3 w-3" /> Assignees</span>
                    <div className="flex flex-wrap gap-1.5">
                      {task.assignee_details.map((a) => (
                        <Badge key={a.id} variant="outline" className="text-[11px] font-medium border-border/40 text-foreground/85">
                          {a.name}
                          {a.role ? <span className="ml-1 text-muted-foreground/55 font-normal">· {a.role}</span> : null}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* 2026-07 — Accountable to (watchers) near Assignees */}
                {(task.accountable_details || task.accountable_to || []).length > 0 && (
                  <div data-testid="task-detail-accountable">
                    <span className="text-muted-foreground text-[11px] block mb-1.5 flex items-center gap-1"><Eye className="h-3 w-3" /> Accountable to</span>
                    <div className="flex flex-wrap gap-1.5">
                      {(task.accountable_details || (task.accountable_to || []).map((id) => members.find((m) => m.id === id) || { id, name: id.slice(0, 8) })).map((a) => (
                        <Badge key={a.id} variant="outline" className="text-[11px] font-medium border-sky-500/30 text-sky-300/90 bg-sky-500/[0.05]">
                          {a.name}
                          {a.role ? <span className="ml-1 text-muted-foreground/55 font-normal">· {a.role}</span> : null}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Client */}
                {(task.client_group_id || task.client_detail) && (() => {
                  const c = task.client_detail || clients.find((cc) => cc.id === task.client_group_id);
                  if (!c) return null;
                  return (
                    <div data-testid="task-detail-client">
                      <span className="text-muted-foreground text-[11px] block mb-1.5">Client</span>
                      <Badge variant="outline" className="text-[11px] font-medium border-gold/30 text-gold bg-gold/[0.04]">
                        {c.name}
                      </Badge>
                    </div>
                  );
                })()}

                {/* Labels — distinct from tags; shown as chips */}
                {(task.labels || []).length > 0 && (
                  <div data-testid="task-detail-labels">
                    <span className="text-muted-foreground text-[11px] block mb-1.5">Labels</span>
                    <div className="flex flex-wrap gap-1">
                      {task.labels.map((l) => (
                        <Badge key={l} variant="outline" className="text-[10.5px] font-normal border-border/40 bg-foreground/[0.04]">
                          {l}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recurrence — only when set & not 'once' */}
                {task.recurrence && task.recurrence !== 'none' && task.recurrence !== '' && task.recurrence !== 'once' && (
                  <div data-testid="task-detail-recurrence">
                    <span className="text-muted-foreground text-[11px] block mb-1.5">Recurrence</span>
                    <Badge variant="outline" className="text-[11px] capitalize border-border/40">{task.recurrence}</Badge>
                  </div>
                )}

                {/* Follow-ups — detailed checklist with dates */}
                {(task.follow_ups || []).length > 0 && (
                  <div data-testid="task-detail-followups">
                    <span className="text-muted-foreground text-[11px] block mb-1.5 flex items-center gap-1"><Calendar className="h-3 w-3" /> Follow-ups</span>
                    <div className="space-y-1">
                      {task.follow_ups.map((fu, i) => (
                        <div
                          key={i}
                          className={`flex items-center gap-2 py-1.5 px-2.5 rounded-md bg-foreground/[0.02] border ${fu.completed ? 'border-green-500/30' : 'border-border/20'}`}
                          data-testid={`task-detail-followup-${i}`}
                        >
                          <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center text-[8px] shrink-0 ${fu.completed ? 'border-green-500 bg-green-500/15 text-green-500' : 'border-gold/50 text-gold'}`}>
                            {fu.completed ? <Check className="h-2.5 w-2.5" /> : i + 1}
                          </div>
                          <span className={`text-[12px] flex-1 ${fu.completed ? 'line-through text-muted-foreground/60' : ''}`}>
                            {fu.label || `Follow-up ${i + 1}`}
                          </span>
                          {fu.date && (
                            <span className="text-[11px] text-muted-foreground/60 font-mono">
                              {formatTaskDate(fu.date)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* External Links */}
                {(task.external_links || []).filter((l) => (typeof l === 'string' ? l : l?.url)).length > 0 && (
                  <div data-testid="task-detail-external-links">
                    <span className="text-muted-foreground text-[11px] block mb-1.5 flex items-center gap-1"><Link2 className="h-3 w-3" /> External Links</span>
                    <div className="space-y-1">
                      {task.external_links.map((l, i) => {
                        const url = typeof l === 'string' ? l : l?.url || '';
                        const name = typeof l === 'string' ? l : (l?.name || l?.url || '');
                        if (!url) return null;
                        return (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[12px] text-gold hover:underline block truncate"
                            data-testid={`task-detail-external-link-${i}`}
                          >
                            {name}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Internal Links */}
                {(task.internal_links || []).filter((l) => l?.path).length > 0 && (
                  <div data-testid="task-detail-internal-links">
                    <span className="text-muted-foreground text-[11px] block mb-1.5 flex items-center gap-1"><Link2 className="h-3 w-3" /> Internal Links</span>
                    <div className="space-y-1">
                      {task.internal_links.map((l, i) => {
                        if (!l?.path) return null;
                        const name = l?.name || l?.path;
                        return (
                          <a
                            key={i}
                            href={l.path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[12px] text-gold hover:underline block truncate"
                            data-testid={`task-detail-internal-link-${i}`}
                          >
                            {name}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Google Drive Link — show with Google Drive icon */}
                {task.drive_link && (
                  <div data-testid="task-detail-drive-link">
                    <span className="text-muted-foreground text-[11px] block mb-1.5">Google Drive</span>
                    <a
                      href={task.drive_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-foreground/[0.03] hover:bg-foreground/[0.06] border border-border/30 hover:border-gold/30 transition-colors text-[12px] text-foreground/90 hover:text-gold"
                      data-testid="task-detail-drive-link-anchor"
                    >
                      {/* Google Drive triangle logo (inline SVG, theme-agnostic colors) */}
                      <svg viewBox="0 0 87.3 78" className="h-3.5 w-3.5 shrink-0" aria-hidden="true">
                        <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da" />
                        <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47" />
                        <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335" />
                        <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d" />
                        <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc" />
                        <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00" />
                      </svg>
                      <span className="font-medium">Google Drive</span>
                    </a>
                  </div>
                )}

                <Separator />

                {/* Description */}
                <div>
                  <span className="text-muted-foreground text-[11px] block mb-2">Description</span>
                  {task.description ? (
                    <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{task.description}</p>
                  ) : (
                    <p className="text-[13px] text-muted-foreground/40">No description</p>
                  )}
                </div>

                {/* 2026-06-16 — Attachments (appears directly below Description per spec). */}
                {(task.attachments || []).length > 0 && (
                  <AttachmentsDisplay
                    attachments={task.attachments}
                    onDelete={async (attachmentId) => {
                      await taskAttachmentAPI.delete(currentWorkspace.id, task.id, attachmentId);
                      setTask((t) => ({ ...t, attachments: (t.attachments || []).filter((a) => !(a && a.id === attachmentId)) }));
                      toast.success('Attachment deleted');
                    }}
                    canDelete={(att) => {
                      if (user?.role === 'super_admin') return true;
                      if (['owner', 'admin', 'manager'].includes(workspaceRole)) return true;
                      // Team — only own attachments.
                      return typeof att === 'object' && att.uploaded_by === user?.id;
                    }}
                    testIdPrefix="task-detail-attachments"
                  />
                )}

                <Separator />

                {/* Status */}
                <div>
                  <span className="text-muted-foreground text-[11px] block mb-2">Status</span>
                  <div className="flex flex-wrap gap-1.5">
                    {STATUSES.map(s => (
                      <Button key={s} size="sm" variant={task.status === s ? 'default' : 'outline'} className="text-[11px] h-7 px-2.5"
                        onClick={() => handleStatusChange(s)} data-testid={`status-${s}`}>
                        {STATUS_LABELS[s]}
                      </Button>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Submissions (Iter 53) — between Description and Comments */}
                <TaskSubmissionsSection
                  workspaceId={currentWorkspace?.id || task.workspace_id}
                  taskId={task.id}
                  viewerId={user?.id}
                  workspaceRole={workspaceRole}
                  focusSubmissionId={focusSubmissionId}
                  focusCommentId={focusCommentId}
                />

                <Separator />

                {/* Comments — 2026-07-13: review tasks (any version uploaded)
                    take feedback ONLY in the per-version threads above. The
                    generic composer is removed there; old comments stay
                    visible read-only for history. */}
                {((task.submissions || []).length === 0) ? (
                  <div>
                    <span className="text-muted-foreground text-[11px] block mb-2 flex items-center gap-1"><MessageSquare className="h-3 w-3" /> Comments ({(task.comments || []).length})</span>
                    <div className="mb-3">
                      <TaskRichCommentBox
                        workspaceId={currentWorkspace?.id || task.workspace_id}
                        taskId={task.id}
                        onPosted={() => loadTask()}
                      />
                    </div>
                    {(task.comments || []).map(c => (
                      <div key={c.id} className="py-2 border-t border-border/30">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[11px] font-medium">{c.user_name}</span>
                          <span className="text-[10px] text-muted-foreground" data-testid="task-comment-timestamp">{formatDateTimeLong(c.created_at)}</span>
                        </div>
                        {c.text && <p className="text-[12px] text-foreground/80">{c.text}</p>}
                        <TaskCommentAttachments attachments={c.attachments} />
                      </div>
                    ))}
                  </div>
                ) : ((task.comments || []).length > 0 && (
                  <div data-testid="legacy-task-comments">
                    <span className="text-muted-foreground text-[11px] block mb-2 flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" /> Older task comments ({(task.comments || []).length}) — read-only. Leave review feedback on a version above.
                    </span>
                    {(task.comments || []).map(c => (
                      <div key={c.id} className="py-2 border-t border-border/30">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[11px] font-medium">{c.user_name}</span>
                          <span className="text-[10px] text-muted-foreground" data-testid="task-comment-timestamp">{formatDateTimeLong(c.created_at)}</span>
                        </div>
                        {c.text && <p className="text-[12px] text-foreground/80">{c.text}</p>}
                        <TaskCommentAttachments attachments={c.attachments} />
                      </div>
                    ))}
                  </div>
                ))}

                <Separator />

                {/* D87 Feature 1 — Transfer + Followers + History */}
                <div data-testid="d87-section-transfer">
                  <TaskTransferPanel
                    workspaceId={currentWorkspace?.id || task.workspace_id}
                    task={{ ...task, __viewer_role: workspaceRole }}
                    viewerId={user?.id}
                    members={members}
                    onChanged={() => { loadTask(); onUpdate && onUpdate(); }}
                  />
                </div>

                <Separator />

                {/* 2026-07 — Legacy subtasks collapse: finish-only child list.
                    Clicking a row opens the child as a full task (links,
                    description, reference content all visible). */}
                <LegacySubtasksSection
                  workspaceId={currentWorkspace?.id || task.workspace_id}
                  taskId={task.id}
                  onOpenTask={(childId) => setTaskId(childId)}
                />

                <Separator />

                {/* Activity */}
                {(task.activity || []).length > 0 && (
                  <div>
                    <span className="text-muted-foreground text-[11px] block mb-2">Activity</span>
                    {task.activity.slice(-5).reverse().map((a, i) => (
                      <div key={i} className="text-[11px] text-muted-foreground py-1">
                        <span className="font-medium text-foreground/70">{a.user_name || 'System'}</span> {a.action}
                        {a.at && <span className="text-muted-foreground/50" data-testid="task-activity-timestamp"> · {formatDateTimeLong(a.at)}</span>}
                        {a.changes && <span className="text-muted-foreground/50"> — {a.changes.join(', ')}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {canDeleteTask && (
                  <>
                    <Separator />
                    <Button type="button" variant="ghost" size="sm" className="text-destructive w-full justify-start text-[12px]" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (confirm('Delete this task? This cannot be undone.')) handleDelete(); }}>
                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete task
                    </Button>
                  </>
                )}
              </div>
            </ScrollArea>
          </>
        )}
        {/* Edit Task Dialog — Phase 3B Batch 1 T-HIGH-1: also gated by
            canEditTaskMetadata to defend against programmatic open by Team. */}
        {showEdit && editData && canEditTaskMetadata && (
          <Dialog open={showEdit} onOpenChange={setShowEdit}>
            <DialogContent className="sm:max-w-[540px] max-h-[85vh] overflow-y-auto bg-card border-border/40">
              <DialogHeader>
                <DialogTitle className="font-display text-[17px]">Edit Task</DialogTitle>
                <DialogDescription className="text-[12px] text-muted-foreground/60">Update task details. Changes save when you press the Save button.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1"><Label className="text-[12px] text-foreground/70">Title *</Label><Input value={editData.title} onChange={e => setEditData(p => ({...p, title: e.target.value}))} className="h-10 text-[14px] bg-foreground/[0.02] border-border/30" /></div>
                <div className="space-y-1"><Label className="text-[12px] text-foreground/70">Description</Label><AutoGrowTextarea value={editData.description} onChange={e => setEditData(p => ({...p, description: e.target.value}))} minRows={6} className="text-[13px]" data-testid="task-edit-description" /></div>
                {/* 2026-06-16 — Attachments in edit mode. Existing files
                    are listed and can be removed/added freely. */}
                <AttachmentsField
                  value={editData.attachments || []}
                  onChange={(next) => setEditData((p) => ({ ...p, attachments: next }))}
                  workspaceId={currentWorkspace?.id}
                  testIdPrefix="task-edit-attachments"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[12px] text-foreground/70">Assignees</Label>
                    {/* Iter 65a — multi-assignee fix: was a single StellarSelect; now uses AssigneeMultiSelect mirroring UnifiedTasksPage. */}
                    <AssigneeMultiSelect
                      selected={editData.assigned_to || []}
                      members={members}
                      onChange={(ids) => setEditData(p => ({ ...p, assigned_to: ids }))}
                      placeholder="Unassigned"
                      showRole
                      testIdPrefix="edit-task-assignees"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[12px] text-foreground/70">Client</Label>
                    <StellarSelect
                      value={editData.client_group_id || '__none__'}
                      onValueChange={(v) => setEditData(p => ({ ...p, client_group_id: v === '__none__' ? '' : v, deliverable_type: v === '__none__' ? '' : p.deliverable_type }))}
                      options={[{ value: '__none__', label: 'No client' }, ...clients.map(c => ({ value: c.id, label: c.name }))]}
                      testId="edit-task-client"
                    />
                  </div>
                </div>
                {/* 2026-07 — Accountable to (multi-select watchers) */}
                <div className="space-y-1">
                  <Label className="text-[12px] text-foreground/70">Accountable to <span className="text-muted-foreground/50 font-normal">(optional — they get notified of updates)</span></Label>
                  <AssigneeMultiSelect
                    selected={editData.accountable_to || []}
                    members={members}
                    onChange={(ids) => setEditData(p => ({ ...p, accountable_to: ids }))}
                    placeholder="No one"
                    showRole
                    testIdPrefix="edit-task-accountable"
                  />
                </div>
                {/* Batch 2 R1 (BUG-RW-CT-017) — Deliverable Type dropdown.
                    Mirror of TaskCreateModal. Only enabled when a client is
                    selected — orphan tasks can't have a deliverable type. */}
                <div className="space-y-1">
                  <Label className="text-[12px] text-foreground/70">Deliverable Type</Label>
                  <StellarSelect
                    value={editData.deliverable_type || 'none'}
                    onValueChange={(v) => setEditData(p => ({ ...p, deliverable_type: v === 'none' ? '' : v }))}
                    disabled={!editData.client_group_id}
                    options={[
                      { value: 'none', label: 'None' },
                      { value: 'post', label: 'Post' },
                      { value: 'story', label: 'Story' },
                      { value: 'reel', label: 'Reel' },
                      { value: 'other', label: 'Other' },
                    ]}
                    testId="edit-task-deliverable-type"
                  />
                  {!editData.client_group_id && (
                    <p className="text-[10px] text-muted-foreground/50">Select a client to enable Deliverable Type.</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[12px] text-foreground/70">Due Date</Label>
                    <StellarDatePicker
                      value={editData.due_date}
                      onChange={(d) => setEditData(p => ({ ...p, due_date: d }))}
                      placeholder="Select due date"
                      testId="edit-task-due-date"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[12px] text-foreground/70 flex items-center gap-1.5">
                      Priority
                      {!editPriorityManuallySet && !task?.priority_locked && editData.due_date && <span className="text-[9px] text-gold/60">auto-suggested</span>}
                      {(editPriorityManuallySet || task?.priority_locked) && <Lock className="h-2.5 w-2.5 text-muted-foreground/40" />}
                    </Label>
                    <div className="flex gap-1.5">
                      {[{v:'high',l:'High',s:'border-red-500 text-red-400 bg-red-500/15',t:'border-red-500/40 text-red-400 bg-transparent',d:'bg-red-500'},{v:'medium',l:'Med',s:'border-gold text-gold bg-gold/15',t:'border-gold/40 text-gold bg-transparent',d:'bg-gold'},{v:'low',l:'Low',s:'border-foreground/30 text-muted-foreground/60 bg-foreground/[0.05]',t:'border-foreground/20 text-muted-foreground/50 bg-transparent',d:'bg-foreground/30'}].map(p => {
                        const locked = editPriorityManuallySet || task?.priority_locked;
                        const isExplicit = locked && editData.priority === p.v;
                        const isTentative = !locked && editData.due_date && editData.priority === p.v;
                        const cls = isExplicit ? p.s : isTentative ? p.t : 'border-border/20 text-muted-foreground/25';
                        return (
                          <button
                            key={p.v}
                            onClick={() => {
                              setEditPriorityTouched(true);
                              if (editPriorityManuallySet && editData.priority === p.v) {
                                // Toggle off: deselect, return to auto-managed
                                setEditPriorityManuallySet(false);
                              } else {
                                setEditPriorityManuallySet(true);
                                setEditData(prev => ({ ...prev, priority: p.v }));
                              }
                            }}
                            className={`flex-1 py-1.5 rounded-lg text-[11px] font-semibold border-2 transition-all flex items-center justify-center gap-1.5 ${cls}`}
                            data-testid={`edit-priority-${p.v}`}
                            aria-pressed={isExplicit}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full ${(isExplicit || isTentative) ? p.d : 'bg-foreground/10'}`} />{p.l}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <button onClick={() => setEditMore(!editMore)} className="text-[12px] text-gold hover:underline font-medium flex items-center gap-1">{editMore ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} {editMore ? 'Less' : 'More options'}</button>
                {editMore && (
                  <div className="space-y-3 pl-3 border-l-2 border-gold/15">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-[11px] text-foreground/60">Status</Label>
                        <StellarSelect
                          value={editData.status}
                          onValueChange={(v) => setEditData(p => ({ ...p, status: v }))}
                          options={[
                            { value: 'not_started', label: 'Not Started' },
                            { value: 'in_progress', label: 'In Progress' },
                            { value: 'review', label: 'Review' },
                            { value: 'completed', label: 'Completed' },
                          ]}
                          size="sm"
                          testId="edit-task-status"
                        />
                      </div>
                      <div className="space-y-1"><Label className="text-[11px] text-foreground/60">Est. Hours</Label><Input type="number" min="0" step="0.5" value={editData.estimated_hours||''} onChange={e => setEditData(p => ({...p, estimated_hours: parseFloat(e.target.value)||0}))} className="h-8 text-[12px] bg-card border-border/30" /></div>
                    </div>
                    <div className="space-y-1"><Label className="text-[11px] text-foreground/60">Labels</Label><Input value={editData.labels.join(', ')} onChange={e => setEditData(p => ({...p, labels: e.target.value.split(',').map(l => l.trim()).filter(Boolean)}))} className="h-8 text-[12px] bg-card border-border/30" placeholder="design, urgent" /></div>
                    <div className="space-y-1">
                      <Label className="text-[11px] text-foreground/60">Recurrence</Label>
                      <StellarSelect
                        value={editData.recurrence || 'once'}
                        onValueChange={(v) => setEditData(p => ({ ...p, recurrence: v }))}
                        options={[
                          { value: 'once', label: 'Once' },
                          { value: 'daily', label: 'Daily' },
                          { value: 'weekly', label: 'Weekly' },
                          { value: 'monthly', label: 'Monthly' },
                        ]}
                        size="sm"
                        testId="edit-task-recurrence"
                      />
                    </div>
                    <div className="space-y-1.5"><Label className="text-[11px] text-foreground/60">External Links</Label>{(editData.external_links||[]).map((l,i) => <div key={i} className="grid grid-cols-2 gap-2"><Input value={l.name} onChange={e => {const ls=[...(editData.external_links||[])];ls[i].name=e.target.value;setEditData(p=>({...p,external_links:ls}));}} placeholder="Name" className="h-7 text-[11px] bg-card border-border/30" /><Input value={l.url} onChange={e => {const ls=[...(editData.external_links||[])];ls[i].url=e.target.value;setEditData(p=>({...p,external_links:ls}));}} placeholder="https://..." className="h-7 text-[11px] bg-card border-border/30" /></div>)}<button onClick={() => setEditData(p => ({...p, external_links: [...(p.external_links||[]),{name:'',url:''}]}))} className="text-[10px] text-gold">+ Add link</button></div>
                    <div className="space-y-1.5"><Label className="text-[11px] text-foreground/60">Internal Links</Label>{(editData.internal_links||[]).map((l,i) => <div key={i} className="grid grid-cols-2 gap-2"><Input value={l.name} onChange={e => {const ls=[...(editData.internal_links||[])];ls[i].name=e.target.value;setEditData(p=>({...p,internal_links:ls}));}} placeholder="Name" className="h-7 text-[11px] bg-card border-border/30" /><Input value={l.path} onChange={e => {const ls=[...(editData.internal_links||[])];ls[i].path=e.target.value;setEditData(p=>({...p,internal_links:ls}));}} placeholder="/path" className="h-7 text-[11px] bg-card border-border/30" /></div>)}<button onClick={() => setEditData(p => ({...p, internal_links: [...(p.internal_links||[]),{name:'',path:''}]}))} className="text-[10px] text-gold">+ Add link</button></div>
                    <div className="space-y-1"><Label className="text-[11px] text-foreground/60">Google Drive Link</Label><Input value={editData.drive_link||''} onChange={e => setEditData(p => ({...p, drive_link: e.target.value}))} className="h-8 text-[12px] bg-card border-border/30" placeholder="https://drive.google.com/..." /></div>
                    <div className="space-y-1.5" data-testid="edit-task-followups-section">
                      <div className="flex items-center justify-between">
                        <Label className="text-[11px] text-foreground/60">Follow-ups (max 3)</Label>
                        {(editData.follow_ups||[]).length < 3 && (
                          <button
                            onClick={() => setEditData(p => ({...p, follow_ups: [...(p.follow_ups||[]), {date:'',label:`Follow-up ${(p.follow_ups||[]).length+1}`,completed:false}]}))}
                            className="text-[10px] text-gold hover:underline font-medium"
                            data-testid="edit-add-followup-btn"
                          >
                            + Add follow-up
                          </button>
                        )}
                      </div>
                      {(editData.follow_ups||[]).map((fu, i) => {
                        const dateAfterDue = fu.date && editData.due_date && fu.date >= editData.due_date;
                        return (
                          <div key={i} className="grid grid-cols-[110px_1fr_28px] gap-2 items-end" data-testid={`edit-followup-row-${i}`}>
                            <div>
                              <span className="text-[9px] text-foreground/40 block mb-1">Date</span>
                              <StellarDatePicker
                                value={fu.date || ''}
                                onChange={(d) => { const fs = [...(editData.follow_ups||[])]; fs[i] = { ...fs[i], date: d }; setEditData(p => ({ ...p, follow_ups: fs })); }}
                                placeholder="Pick"
                                size="sm"
                                maxDate={editData.due_date || undefined}
                                clearable={false}
                              />
                              {dateAfterDue && <span className="text-[9px] text-red-400 block mt-0.5">Must be before due date</span>}
                            </div>
                            <div>
                              <span className="text-[9px] text-foreground/40 block mb-1">Label</span>
                              <Input value={fu.label||''} onChange={e => {const fs=[...(editData.follow_ups||[])];fs[i].label=e.target.value;setEditData(p=>({...p,follow_ups:fs}));}} className="h-8 text-[11px] bg-card border-border/30" />
                            </div>
                            <button
                              onClick={() => setEditData(p => ({...p, follow_ups: (p.follow_ups||[]).filter((_,j)=>j!==i)}))}
                              className="h-8 w-7 flex items-center justify-center text-muted-foreground/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              aria-label="Remove follow-up"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter><Button variant="ghost" onClick={() => setShowEdit(false)}>Cancel</Button><Button className="bg-gold/10 hover:bg-gold/20 text-gold border-0" onClick={saveEdit} data-testid="save-task-edit">Save Changes</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </SheetContent>
    </Sheet>
  );
}
